import { useEffect, useMemo, useState } from 'react'
import { InputGroupKeys } from 'store/customer/enums'
import { ZeroOrPositiveNumbersWithNoDecimals } from 'constants/regex'
import { useTranslation } from 'next-i18next/pages'
import { RegisterOptions, useFormContext, useWatch } from 'react-hook-form'
import { MIN_USAGE_FOR_USUAL } from 'features/simulation/Simulation.constants'
import { Banner, Checkbox, Form, Icon, TextField, Tooltip } from '@boltenergy-be/design-system'
import { checkHasInjectionUsageFields, checkHasSolarKva } from 'features/simulation/utils'
import { UsageStepFormInputs } from 'features/simulation/steps/types'
import { CustomUsageFormProps } from 'features/usage-form/types'
import { convertAmountOfSolarPanelsToKva, convertKvaToInjection } from 'utils/conversion'
import { useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { MAX_SOLAR_KVA } from 'constants/usages'
import UsageInputLabelComponent from 'features/usage-form/custom-usage-form/usage-input-label-component/UsageInputLabelComponent'
import CheckboxList from 'components/checkbox-list/CheckboxList'
import { selectIsBoltDriveFlexFlow } from 'features/bolt-drive-flex/selectors'
import { COUNTER_TYPES, METER_TYPES, REGIONS } from '@boltenergy-be/shared'
import InjectionRoiBanner from 'features/usage-form/custom-usage-form/injection-roi-banner/InjectionRoiBanner'
import { CUSTOM_USAGE_FORM_ID } from 'features/usage-form/constants'

const CustomUsageForm = ({ onBlur, onSubmit, needsGas, hasInjectionRoi }: CustomUsageFormProps) => {
  // Redux
  const marketingStore = useStoreSelector((store) => store.marketing)
  const { region } = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const isBoltDriveFlexFlow = selectIsBoltDriveFlexFlow(marketingStore)

  // i18n
  const { t } = useTranslation(['simulation', 'common', 'validation', 'registration'])

  // React Hook Form
  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useFormContext<UsageStepFormInputs>()
  const watchCounterType = useWatch({ control, name: 'counterType' })
  const watchElectricity = useWatch({ control, name: 'electricity' })
  const watchGas = useWatch({ control, name: 'gas' })
  const watchHasExclNight = useWatch({ control, name: 'hasExclNight' })
  const watchHasSolarPanels = useWatch({ control, name: 'hasSolarPanels' })
  const watchMeterType = useWatch({ control, name: 'meterType' })
  const watchSolarPanels = useWatch({ control, name: 'solarPanels' })
  const watchWalloniaSolarPanelsSince2024 = useWatch({ control, name: 'walloniaSolarPanelsSince2024' })
  const watchSolarKva = useWatch({ control, name: 'solarKva' })

  // Local state
  const [showSolarPanels, setShowSolarPanels] = useState<boolean>(!!watchSolarPanels)

  // Constants
  const isSingleMeter = watchMeterType === METER_TYPES.single
  const isBru = region === REGIONS.brussels
  const isAnalog = watchCounterType === COUNTER_TYPES.analog
  const hasInjectionUsageFields = useMemo<boolean>(
    () =>
      checkHasInjectionUsageFields({
        counterType: watchCounterType,
        hasSolarPanels: watchHasSolarPanels
      }),
    [watchCounterType, watchHasSolarPanels]
  )
  const hasSolarKva = useMemo<boolean>(
    () =>
      checkHasSolarKva({
        counterType: watchCounterType,
        hasSolarPanels: watchHasSolarPanels,
        walloniaSolarPanelsSince2024: watchWalloniaSolarPanelsSince2024
      }),
    [watchCounterType, watchHasSolarPanels, watchWalloniaSolarPanelsSince2024]
  )

  /**
   * Saves the solar panels input in the store
   *
   * @param {number | null} solarPanels
   */
  const saveSolarPanelsInput = (solarPanels: number | null) => {
    setValue('solarPanels', solarPanels || undefined)

    // Calculate solar power (in kva) based on given solar panels
    const solarPowerKva = convertAmountOfSolarPanelsToKva(solarPanels)
    setValue('solarKva', Math.round(solarPowerKva * 100) / 100, { shouldValidate: true })

    // Calculate injection
    const injection = convertKvaToInjection(solarPowerKva)

    // Set single meter injection
    setValue('electricity.injection.single', Math.round(injection), { shouldValidate: true })

    const solarPowerKwhDay = injection * 0.66
    setValue('electricity.injection.double.day', Math.round(solarPowerKwhDay), { shouldValidate: true })
    setValue('electricity.injection.double.night', Math.round(injection - solarPowerKwhDay), { shouldValidate: true })
  }

  /**
   * Returns the validation object for a given label
   *
   * @param {string} label
   * @param {boolean} required
   * @returns {RegisterOptions<UsageStepFormInputs>}
   */
  const getValidationObject = (label: string, required: boolean = true): RegisterOptions<UsageStepFormInputs> => {
    return {
      min: { value: 0, message: t('validation:min.number', { attribute: label, min: 0 }) },
      pattern: { value: ZeroOrPositiveNumbersWithNoDecimals, message: t('validation:noDecimals', { attribute: label }) },
      ...(required && {
        required: t('validation:required')
      })
    }
  }

  /**
   * Checks if the usage is unusual
   *
   * @param {number} [usage]
   * @returns {boolean}
   */
  const isUnusualUsage = (usage?: number): boolean => {
    return !(watchHasSolarPanels && isAnalog) && usage > 0 && usage < MIN_USAGE_FOR_USUAL
  }

  useEffect(() => {
    if (isBoltDriveFlexFlow && watchCounterType !== COUNTER_TYPES.digital) {
      setValue('counterType', COUNTER_TYPES.digital)
    }
  }, [isBoltDriveFlexFlow, watchCounterType])

  return (
    <Form id={CUSTOM_USAGE_FORM_ID} onSubmit={handleSubmit(onSubmit)} onBlur={onBlur && isValid ? () => onBlur(getValues()) : undefined}>
      {isBoltDriveFlexFlow && <Banner type="informative" as="section" title={t('common:driveFlexWarnings.digitalMeter')} />}

      <CheckboxList>
        <Checkbox
          className="grid-col-full"
          checked={watchMeterType === METER_TYPES.double}
          id="meter"
          label={t('simulation:steps.usage.form.hasDoubleRate')}
          name="options"
          onChange={() => setValue('meterType', watchMeterType === METER_TYPES.double ? METER_TYPES.single : METER_TYPES.double)}
        />

        <Checkbox
          checked={watchCounterType === COUNTER_TYPES.digital}
          disabled={(region === REGIONS.wallonia && watchWalloniaSolarPanelsSince2024) || isBoltDriveFlexFlow}
          id="counterType"
          label={t('simulation:steps.usage.form.hasDigital')}
          name="options"
          onChange={() => {
            if (isBru && watchCounterType === COUNTER_TYPES.digital) {
              setValue('hasSolarPanels', false)
            } else if (region === REGIONS.wallonia && watchCounterType === COUNTER_TYPES.digital) {
              setValue('walloniaSolarPanelsSince2024', false)
            }

            setValue('counterType', watchCounterType === COUNTER_TYPES.digital ? COUNTER_TYPES.analog : COUNTER_TYPES.digital)
          }}
        />

        <Checkbox
          checked={watchHasExclNight}
          id="hasExclNight"
          label={t('simulation:steps.usage.form.hasExclNight')}
          name="options"
          onChange={() => setValue('hasExclNight', !watchHasExclNight)}
        />

        <Checkbox
          checked={watchHasSolarPanels}
          disabled={isBru && isAnalog}
          id="hasSolarPanels"
          label={
            <span>
              {t('simulation:steps.usage.form.hasSolarPanels')}{' '}
              {isBru && isAnalog && (
                <Tooltip tooltipContent={t('simulation:steps.usage.form.noSolarPanelsBruAnalog')}>
                  <Icon name="questionMarkCircle" />
                </Tooltip>
              )}
            </span>
          }
          name="options"
          onChange={() => setValue('hasSolarPanels', !watchHasSolarPanels)}
        />

        {region === REGIONS.wallonia && watchHasSolarPanels && (
          <Checkbox
            checked={watchWalloniaSolarPanelsSince2024}
            id="walloniaSolarPanelsSince2024"
            label={t('common:walloniaSolarPanelsSince2024')}
            name="options"
            onChange={() => {
              setValue('walloniaSolarPanelsSince2024', !watchWalloniaSolarPanelsSince2024)
              if (!watchWalloniaSolarPanelsSince2024) {
                setValue('counterType', COUNTER_TYPES.digital)
              }
            }}
          />
        )}
      </CheckboxList>

      {/* ELECTRICITY CONSUMPTION */}

      {isSingleMeter ? (
        // SINGLE RATE
        <TextField
          {...register('electricity.consumption.single', getValidationObject(t('simulation:steps.usage.form.electricity')))}
          error={errors?.electricity?.consumption?.single?.message}
          grid="full"
          label={t('simulation:steps.usage.form.electricity')}
          type="number"
          step={1}
          suffix={{
            as: 'kWh'
          }}
          message={
            isUnusualUsage(watchElectricity?.consumption?.single)
              ? { type: 'warning', text: t('simulation:steps.usage.alerts.unusualUsage') }
              : undefined
          }
        />
      ) : (
        // DOUBLE RATE
        <>
          <TextField
            {...register('electricity.consumption.double.day', getValidationObject(t('simulation:steps.usage.form.dayMeter')))}
            error={errors?.electricity?.consumption?.double?.day?.message}
            grid="full"
            label={t('simulation:steps.usage.form.dayMeter')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
            message={
              isUnusualUsage(watchElectricity?.consumption?.double?.day)
                ? { type: 'warning', text: t('simulation:steps.usage.alerts.unusualUsage') }
                : undefined
            }
          />

          <TextField
            {...register('electricity.consumption.double.night', getValidationObject(t('simulation:steps.usage.form.nightMeter')))}
            error={errors?.electricity?.consumption?.double?.night?.message}
            grid="full"
            label={t('simulation:steps.usage.form.nightMeter')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
            message={
              isUnusualUsage(watchElectricity?.consumption?.double?.night)
                ? { type: 'warning', text: t('simulation:steps.usage.alerts.unusualUsage') }
                : undefined
            }
          />
        </>
      )}

      {watchHasExclNight && (
        <TextField
          {...register('electricity.consumption.exclusiveNight', getValidationObject(t('simulation:steps.usage.form.exclNight')))}
          error={errors?.electricity?.consumption?.exclusiveNight?.message}
          grid="full"
          label={t('simulation:steps.usage.form.exclNight')}
          type="number"
          step={1}
          suffix={{
            as: 'kWh'
          }}
          message={
            isUnusualUsage(watchElectricity?.consumption?.exclusiveNight)
              ? { type: 'warning', text: t('simulation:steps.usage.alerts.unusualUsage') }
              : undefined
          }
        />
      )}

      {/* ELECTRICITY INJECTION */}

      {watchHasSolarPanels && (hasSolarKva || hasInjectionUsageFields) && (
        <>
          {showSolarPanels ? (
            <TextField
              {...register('solarPanels', getValidationObject(t('simulation:steps.usage.form.injectionNight')))}
              error={errors?.solarPanels?.message}
              grid="full"
              label={t('simulation:steps.usage.form.solarPanels')}
              labelComponentSlot={
                <UsageInputLabelComponent
                  label={
                    hasInjectionUsageFields ? t('simulation:steps.usage.form.injectionKnown') : t('simulation:steps.usage.form.kvaKnown')
                  }
                  onClick={() => setShowSolarPanels(false)}
                />
              }
              onChange={(e) => saveSolarPanelsInput(e.target.value ? Number(e.target.value) : null)}
              type="number"
              step={1}
            />
          ) : (
            <>
              {hasSolarKva && (
                // SOLAR KVA
                <TextField
                  {...register('solarKva', {
                    max: {
                      value: MAX_SOLAR_KVA,
                      message: t('validation:max.number', { attribute: t('simulation:steps.usage.form.solarKva'), max: MAX_SOLAR_KVA })
                    },
                    min: {
                      value: 0,
                      message: t('validation:min.number', { attribute: t('simulation:steps.usage.form.solarKva'), min: 0 })
                    },
                    required: t('validation:required')
                  })}
                  error={errors?.solarKva?.message}
                  grid="full"
                  label={t('simulation:steps.usage.form.solarKva')}
                  labelComponentSlot={
                    <UsageInputLabelComponent
                      label={t('common:dontKnow')}
                      onClick={() => setShowSolarPanels(true)}
                      tooltip={t('simulation:steps.usage.form.solarKvaInfo')}
                    />
                  }
                  type="number"
                  step={0.01}
                  suffix={{
                    as: 'kVA'
                  }}
                />
              )}

              {hasInjectionUsageFields && (
                // INJECTION FIELDS
                <>
                  {isSingleMeter ? (
                    <TextField
                      {...register('electricity.injection.single', getValidationObject(t('simulation:steps.usage.form.injection')))}
                      error={errors?.electricity?.injection?.single?.message}
                      grid="full"
                      label={t('simulation:steps.usage.form.injection')}
                      labelComponentSlot={
                        <UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />
                      }
                      type="number"
                      step={1}
                      suffix={{
                        as: 'kWh'
                      }}
                    />
                  ) : (
                    <>
                      <TextField
                        {...register(
                          'electricity.injection.double.day',
                          getValidationObject(t('simulation:steps.usage.form.injectionDay'))
                        )}
                        error={errors?.electricity?.injection?.double?.day?.message}
                        grid="full"
                        label={t('simulation:steps.usage.form.injectionDay')}
                        labelComponentSlot={
                          <UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />
                        }
                        type="number"
                        step={1}
                        suffix={{
                          as: 'kWh'
                        }}
                      />

                      <TextField
                        {...register(
                          'electricity.injection.double.night',
                          getValidationObject(t('simulation:steps.usage.form.injectionNight'))
                        )}
                        error={errors?.electricity?.injection?.double?.night?.message}
                        grid="full"
                        label={t('simulation:steps.usage.form.injectionNight')}
                        labelComponentSlot={
                          <UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />
                        }
                        type="number"
                        step={1}
                        suffix={{
                          as: 'kWh'
                        }}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}

          {hasInjectionRoi && (
            <InjectionRoiBanner
              solarKva={hasSolarKva && !showSolarPanels ? Number(watchSolarKva) : 0}
              amountOfSolarPanels={showSolarPanels ? Number(watchSolarPanels) : 0}
              injection={
                hasInjectionUsageFields && !showSolarPanels
                  ? isSingleMeter
                    ? Number(watchElectricity?.injection?.single)
                    : (Number(watchElectricity?.injection?.double?.day) || 0) + (Number(watchElectricity?.injection?.double?.night) || 0)
                  : 0
              }
            />
          )}
        </>
      )}

      {/* GAS */}

      {needsGas && (
        <TextField
          {...register('gas', getValidationObject(t('simulation:steps.usage.form.gas'), needsGas))}
          error={errors?.gas?.message}
          grid="full"
          label={t('simulation:steps.usage.form.gas')}
          type="number"
          step={1}
          suffix={{
            as: 'kWh'
          }}
          message={
            !watchGas && watchGas > 0 && watchGas < MIN_USAGE_FOR_USUAL
              ? { type: 'warning', text: t('simulation:steps.usage.alerts.unusualUsage') }
              : undefined
          }
        />
      )}
    </Form>
  )
}

export default CustomUsageForm
